import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * SOUND-661 — files, filtered and paginated SERVER-side.
 *
 * Was: fetch everything for the company, filter by category in the browser. That
 * could not scale past the repo's silent `limit = 100`, and the category filter
 * only knew the 8 values FilesTab hardcoded, out of 157 in prod.
 *
 * `filters` is the useSearch shape, so the tab and the standalone list page can
 * share one hook. Pagination is opt-in on the server: omit page/limit and you
 * get everything, which is what a tab with no pagination control needs.
 */
export function useFiles({
  companyId: companyIdProp,
  filters = {},
  page = null,
  limit = null,
  sort = "-createdAt",
  // SOUND-661: `"all"` asks the server for files ACROSS companies — the /files
  // list page. Internal-only and enforced server-side; a client or partner
  // sending it is ignored, so this is a request, not a grant.
  scope = null,
} = {}) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const companyId = companyIdProp || effectiveCompanyId;

  const [files, setFiles] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 0, total: 0, totalPages: 1 });
  // SOUND-295: aggregated SERVER-side over the same filter as the page. A KPI
  // computed from the returned rows is wrong the moment anything paginates.
  const [summary, setSummary] = useState({ totalBytes: 0, companyCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Primitives, not the `filters` object — a new object identity on every render
  // would re-fire the fetch forever.
  const { search = "", category = "", companyId: filterCompanyId = "" } = filters;

  const buildParams = useCallback(
    (overrides = {}) => {
      const params = new URLSearchParams();
      params.set("sortBy", sort.replace(/^-/, ""));
      params.set("sortDir", sort.startsWith("-") ? "desc" : "asc");
      // Only sent when a caller explicitly asks to cross companies. Sending it
      // by default would make every Files tab a cross-company read.
      if (scope) params.set("scope", scope);
      if (search) params.set("search", search);
      if (category) params.set("category", category);
      // Only meaningful on the unscoped list; the tab forces its own company.
      if (filterCompanyId) params.set("companyId", filterCompanyId);
      if (page !== null) params.set("page", String(page));
      if (limit !== null) params.set("limit", String(limit));
      Object.entries(overrides).forEach(([k, v]) => params.set(k, String(v)));
      return params;
    },
    [sort, search, category, filterCompanyId, page, limit, scope]
  );

  const fetchFiles = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);

      const response = await makeApiRequest(
        "get",
        `${apiRoutes.listFiles}?${buildParams().toString()}`,
        auth.user,
        companyId
      );

      setFiles(response?.data?.data || []);
      setPagination(
        response?.data?.pagination || { page: 1, limit: 0, total: 0, totalPages: 1 }
      );
      setSummary(response?.data?.summary || { totalBytes: 0, companyCount: 0 });
    } catch (err) {
      console.error("Failed to fetch files", err);
      setError("Failed to load files");
    } finally {
      setLoading(false);
    }
  }, [companyId, buildParams, auth.user]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  /**
   * Every fileId matching the CURRENT filters, ignoring the page.
   *
   * "Download All" meant all of a company's files before this page paginated.
   * Wiring the button to the visible rows would have quietly turned it into
   * "download 25 of 61" with nothing on screen saying so — the same silent cap
   * this ticket removed from the repo layer. Omitting page/limit asks the server
   * for the unbounded set, which its contract supports.
   */
  const fetchAllRows = useCallback(async () => {
    if (!companyId) return [];
    const params = buildParams();
    params.delete("page");
    params.delete("limit");
    const response = await makeApiRequest(
      "get",
      `${apiRoutes.listFiles}?${params.toString()}`,
      auth.user,
      companyId
    );
    return response?.data?.data || [];
  }, [companyId, buildParams, auth.user]);

  /** Ids only — what the zip endpoint takes. */
  const fetchAllIds = useCallback(
    async () => (await fetchAllRows()).map((f) => f.fileId),
    [fetchAllRows]
  );

  /**
   * Upload files with optional per-file metadata.
   * @param {FileList|File[]} fileList
   * @param {string} defaultCategory - fallback category
   * @param {Object[]} [perFileMeta] - [{displayName, category}] matching fileList order
   */
  const uploadFiles = async (fileList, defaultCategory = "general", perFileMeta = []) => {
    if (!companyId || !fileList?.length) return [];
    try {
      const formData = new FormData();
      Array.from(fileList).forEach((file) => formData.append("files", file));
      formData.append("category", defaultCategory);
      if (perFileMeta.length > 0) {
        formData.append("metadata", JSON.stringify(perFileMeta));
      }

      const response = await makeApiRequest(
        "post",
        apiRoutes.uploadFiles,
        auth.user,
        companyId,
        formData
      );

      const uploaded = response?.data?.data || [];
      await fetchFiles();
      return uploaded;
    } catch (err) {
      console.error("Upload failed", err);
      throw err;
    }
  };

  /**
   * SOUND-661 — `ownerCompanyId` is the FILE's company, not the page's.
   *
   * Every mutation below scope-checks `file.companyId !== companyId` server-side.
   * On the cross-company /files list the hook's own companyId is `sound-admin`,
   * so editing another company's file would fail that check and 404. The row
   * carries its companyId, so the caller passes it and the header names the
   * company that actually owns the record. The Files tab passes nothing and is
   * unchanged.
   */
  const deleteFile = async (fileId, ownerCompanyId = null) => {
    if (!fileId) return false;
    try {
      await makeApiRequest(
        "delete",
        apiRoutes.deleteFile(fileId),
        auth.user,
        ownerCompanyId || companyId
      );
      setFiles((prev) => prev.filter((f) => f.fileId !== fileId));
      return true;
    } catch (err) {
      console.error("Delete failed", err);
      return false;
    }
  };

  const renameFile = async (fileId, displayName, ownerCompanyId = null) => {
    if (!fileId || !displayName) return false;
    try {
      const response = await makeApiRequest(
        "put",
        apiRoutes.updateFile(fileId),
        auth.user,
        ownerCompanyId || companyId,
        { displayName }
      );
      const updated = response?.data?.data;
      if (updated) {
        setFiles((prev) =>
          prev.map((f) => (f.fileId === fileId ? { ...f, displayName } : f))
        );
      }
      return true;
    } catch (err) {
      console.error("Rename failed", err);
      return false;
    }
  };

  const moveFile = async (fileId, newCategory, ownerCompanyId = null) => {
    if (!fileId || !newCategory) return false;
    try {
      await makeApiRequest(
        "put",
        apiRoutes.updateFile(fileId),
        auth.user,
        ownerCompanyId || companyId,
        { category: newCategory }
      );
      await fetchFiles();
      return true;
    } catch (err) {
      console.error("Move failed", err);
      return false;
    }
  };

  const downloadFile = async (fileId) => {
    if (!fileId) return;
    try {
      const response = await makeApiRequest(
        "get",
        apiRoutes.downloadFile(fileId),
        auth.user,
        companyId
      );
      const url = response?.data?.signedUrl;
      if (url) window.open(url, "_blank");
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  const getPreviewUrl = async (fileId) => {
    if (!fileId) return null;
    try {
      const response = await makeApiRequest(
        "get",
        `${apiRoutes.downloadFile(fileId)}?inline=true`,
        auth.user,
        companyId
      );
      return response?.data?.signedUrl || null;
    } catch (err) {
      console.error("Preview URL failed", err);
      return null;
    }
  };

  const downloadAll = async (fileIds) => {
    if (!fileIds?.length) return;
    try {
      const response = await makeApiRequest(
        "post",
        apiRoutes.downloadFilesZip,
        auth.user,
        companyId,
        { fileIds },
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "files.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download all failed", err);
    }
  };

  return {
    files,
    pagination,
    summary,
    fetchAllRows,
    fetchAllIds,
    loading,
    error,
    uploadFiles,
    deleteFile,
    renameFile,
    moveFile,
    downloadFile,
    downloadAll,
    getPreviewUrl,
    refetch: fetchFiles,
  };
}

/**
 * SOUND-661 — the category vocabulary, from the API.
 *
 * Returns what is PRESENT in scope, so the filter can always reach everything on
 * screen. `all: true` asks for the full canonical list instead, which is what an
 * upload or move picker needs — those offer every choice, not only the ones that
 * already exist.
 *
 * Never hardcode this list. FilesTab hardcoded 8 of 157 values, which is why
 * 41.7% of files were unreachable and rendered as raw camelCase.
 */
export function useFileCategories({ companyId: companyIdProp, all = false, scope = null } = {}) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const companyId = companyIdProp || effectiveCompanyId;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!companyId) return undefined;

    (async () => {
      try {
        setLoading(true);
        const qs = new URLSearchParams();
        if (all) qs.set("all", "true");
        if (scope) qs.set("scope", scope);
        const url = qs.toString()
          ? `${apiRoutes.fileCategories}?${qs.toString()}`
          : apiRoutes.fileCategories;
        const response = await makeApiRequest("get", url, auth.user, companyId);
        if (!cancelled) setCategories(response?.data?.data || []);
      } catch (err) {
        // A failed vocabulary fetch must not blank the page. An empty list means
        // the filter offers nothing, which is degraded but honest — the table
        // still renders every file.
        console.error("Failed to fetch file categories", err);
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, all, scope, auth.user]);

  return { categories, loading };
}
